
import React from 'react';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Flame, FileText } from 'lucide-react';
import DataExtractionTool from '@/components/DataExtractionTool';

const GasReports = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-auto">
      <Header />
      <main className="pt-24 pb-10 w-full mx-0 px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Recent Gas Reports</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto">
                    {recentReports.map((report, index) => (
                      <div key={index} className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <div className={`w-2 h-2 rounded-full mr-3 ${reportStatusColors[report.status]}`}></div>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">{report.title}</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{report.date}</p>
                        </div>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{report.consumption} m³</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Gas Usage Insights</CardTitle>
                  <Flame className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-[420px] overflow-y-auto">
                    {usageInsights.map((insight, index) => (
                      <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4 last:border-0 last:pb-0">
                        <h4 className="text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">{insight.title}</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{insight.description}</p>
                        <div className="mt-2 flex items-center">
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                            <div 
                              className="bg-orange-500 h-full rounded-full" 
                              style={{ width: `${insight.percentage}%` }}
                            ></div>
                          </div>
                          <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">{insight.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white dark:bg-[#0B101C] overflow-hidden">
            <DataExtractionTool className="h-full" />
          </div>
        </div>
      </main>
    </div>
  );
};

// Sample data for the reports page
const recentReports = [
  {
    title: "Monthly Gas Usage Report",
    date: "June 1, 2023",
    consumption: 250,
    status: "completed"
  },
  {
    title: "Quarterly Gas Analysis",
    date: "April 15, 2023",
    consumption: 780,
    status: "completed"
  },
  {
    title: "Annual Gas Conservation Report",
    date: "January 10, 2023",
    consumption: 3120,
    status: "pending"
  },
  {
    title: "Winter Peak Usage Analysis",
    date: "February 5, 2023",
    consumption: 1050,
    status: "processing"
  },
  {
    title: "Gas Leak Detection Report",
    date: "July 22, 2022",
    consumption: 320,
    status: "completed"
  }
];

const reportStatusColors = {
  completed: "bg-green-500",
  pending: "bg-amber-500",
  processing: "bg-blue-500"
};

const usageInsights = [
  {
    title: "Heating",
    description: "Heating accounts for 52% of your total gas consumption.",
    percentage: 52
  },
  {
    title: "Cooking",
    description: "Kitchen gas usage represents 25% of your total consumption.",
    percentage: 25
  },
  {
    title: "Hot Water",
    description: "Hot water heating contributes to 18% of your total gas usage.",
    percentage: 18
  },
  {
    title: "Other Appliances",
    description: "Other gas appliances make up 5% of usage.",
    percentage: 5
  }
];

export default GasReports;
